/**
 * Censura de credenciales para todo lo que se envía a Sentry.
 *
 * Los errores de axios se pasan enteros a `console.error` en cientos de sitios
 * y el token de sesión (`config.headers.Authorization`) llega a Sentry por tres
 * caminos distintos:
 *
 *  1. el `message` del log de consola, un string ya normalizado a
 *     `normalizeDepth` (3) por `consoleLoggingIntegration`;
 *  2. los `attributes` del mismo log, donde `sentry.message.parameter.N` guarda
 *     el objeto CRUDO cuando se llama `console.error('texto:', err)`;
 *  3. los breadcrumbs de consola que viajan en cada evento de error
 *     (`data.arguments`), también con el objeto crudo.
 *
 * Por eso hay que censurar strings y objetos, y no alcanza con bajar
 * `normalizeDepth`.
 */

/**
 * Claves cuyo valor nunca debe salir del browser. Se comparan contra el nombre
 * de la propiedad sin distinguir mayúsculas, así que cubre `Authorization` y
 * `authorization` (axios normaliza distinto según el transporte).
 */
const SENSITIVE_KEY =
  /^(authorization|proxy-authorization|cookie|set-cookie|x-api-key|id_?token|access_?token|refresh_?token|certpass|password)$/i;

/** `Bearer <token>` dentro de un string ya serializado. */
const BEARER = /\bBearer\s+[\w\-.~+/]+=*/gi;

/** JWT suelto: los ID tokens de Firebase empiezan siempre en `eyJ`. */
const JWT = /\beyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]+/g;

export const REDACTED = '[Redacted]';

const MAX_DEPTH = 8;

/**
 * Censura tokens dentro de un string.
 *
 * @param {unknown} value
 * @returns {unknown} el string censurado, o `value` intacto si no era string
 */
export const redactString = (value) =>
  typeof value === 'string' ? value.replace(BEARER, `Bearer ${REDACTED}`).replace(JWT, REDACTED) : value;

/**
 * Recorre un valor arbitrario censurando credenciales. Muta in place cuando
 * puede: los eventos de Sentry traen objetos que no conviene clonar.
 *
 * @param {unknown} value
 * @param {WeakSet<object>} [seen] control de ciclos (`error.request.response`
 *   se autoreferencia, así que sin esto un error de axios cuelga el recorrido)
 * @param {number} [depth]
 * @returns {unknown} el mismo valor, censurado
 */
export const redactSecrets = (value, seen = new WeakSet(), depth = 0) => {
  if (typeof value === 'string') return redactString(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH || seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, i) => {
      value[i] = redactSecrets(item, seen, depth + 1);
    });
    return value;
  }

  Object.keys(value).forEach((key) => {
    if (SENSITIVE_KEY.test(key)) {
      value[key] = REDACTED;
      return;
    }
    try {
      value[key] = redactSecrets(value[key], seen, depth + 1);
    } catch {
      // getters que lanzan o propiedades read-only: se deja el valor original
    }
  });

  return value;
};
